/**
 * « Nouvelle version — Rafraîchir » : le maillon qui manquait à la chaîne de mise à jour.
 *
 * **Pourquoi ce fichier existe.** Deux correctifs ont déjà porté sur les mises à jour :
 * la 0.10.4 a empêché Cloudflare de répondre du HTML pour un morceau absent, la 0.10.5 a
 * fait passer le document en `NetworkFirst` pour qu'un rechargement serve toujours l'index
 * frais. Il restait un trou au milieu : le service worker se met à jour en silence, mais
 * **la page continue de tourner sur l'ancien code** jusqu'au prochain rechargement, sans
 * que rien ne le dise. Un joueur en session longue reste donc sur une version périmée
 * pendant des heures, et un correctif déployé ne l'atteint jamais.
 *
 * Le réglage `registerType: 'autoUpdate'` appelle `skipWaiting()` et `clientsClaim()` : le
 * nouveau worker s'active et prend le contrôle de la page tout de suite. C'est
 * `controllerchange` qui le signale, et c'est donc lui le déclencheur principal.
 *
 * ⚠️ **Le piège du premier chargement.** Sur une toute première visite, il n'y a aucun
 * worker, puis un s'installe et prend le contrôle : `controllerchange` se déclenche aussi
 * dans ce cas. Annoncer « nouvelle version » à quelqu'un qui découvre le jeu serait
 * absurde. On mémorise donc s'il existait DÉJÀ un contrôleur au moment où l'on se met à
 * écouter, et on ne dit rien sinon.
 *
 * ⚠️ Rien de tout cela n'est observable en développement : `devOptions.enabled` est à
 * `false` dans `vite.config.ts`, donc aucun service worker n'est enregistré par
 * `npm run dev`. Ce comportement se vérifie **en production**, ce que la règle 11 demande
 * de toute façon.
 */

import { t } from '@/i18n';
import { createButton } from './components';
import { icon } from './icons';

/**
 * Fréquence de vérification active.
 *
 * Le navigateur ne cherche une nouvelle version que lors d'une navigation. Un onglet
 * laissé ouvert — cas normal pour un idle, qui se joue justement en le laissant tourner —
 * ne verrait jamais un déploiement sans cet appel. Trente minutes : assez pour qu'un
 * correctif parvienne au joueur dans l'heure, assez peu pour rester invisible.
 */
const CHECK_EVERY = 30 * 60 * 1000;

/**
 * Surveille l'arrivée d'une nouvelle version et appelle `onFound` **une seule fois**.
 *
 * Trois signaux, parce qu'aucun ne couvre tous les cas :
 *
 * 1. `controllerchange` — le nouveau worker a pris le contrôle (cas normal en
 *    `autoUpdate`) ;
 * 2. un worker **déjà en attente** à l'ouverture : la page était ouverte pendant un
 *    déploiement précédent, et l'événement est passé avant qu'on écoute ;
 * 3. `updatefound` puis `statechange` — un worker s'installe pendant la session, ce qui
 *    couvre le cas où `skipWaiting` serait un jour retiré de la configuration.
 *
 * @returns de quoi arrêter la surveillance.
 */
export function watchForUpdates(onFound: () => void): () => void {
  if (!('serviceWorker' in navigator)) return () => undefined;

  const container = navigator.serviceWorker;
  // Y avait-il déjà un worker aux commandes ? Sinon, ce qui suit est une première
  // installation et non une mise à jour.
  const wasControlled = container.controller !== null;

  let stopped = false;
  let announced = false;
  let timer: ReturnType<typeof setInterval> | null = null;

  const announce = (): void => {
    if (stopped || announced || !wasControlled) return;
    announced = true;
    onFound();
  };

  const onControllerChange = (): void => {
    announce();
  };
  container.addEventListener('controllerchange', onControllerChange);

  void container
    .getRegistration()
    .then((registration) => {
      if (registration === undefined || stopped) return;

      // Cas 2 : la mise à jour attend déjà, l'événement a eu lieu avant nous.
      if (registration.waiting !== null) announce();

      // Cas 3 : un worker s'installe pendant qu'on joue.
      registration.addEventListener('updatefound', () => {
        const installing = registration.installing;
        if (installing === null) return;
        installing.addEventListener('statechange', () => {
          if (installing.state === 'installed' || installing.state === 'activated') announce();
        });
      });

      timer = setInterval(() => {
        // `update()` peut échouer hors ligne : sans ce garde-fou, une coupure réseau
        // remonterait une promesse rejetée à chaque demi-heure.
        void registration.update().catch(() => undefined);
      }, CHECK_EVERY);
    })
    .catch(() => undefined);

  return () => {
    stopped = true;
    container.removeEventListener('controllerchange', onControllerChange);
    if (timer !== null) clearInterval(timer);
  };
}

/** Recharge la page sur la nouvelle version. */
async function refresh(): Promise<void> {
  try {
    if (!('serviceWorker' in navigator)) {
      location.reload();
      return;
    }
    const registration = await navigator.serviceWorker.getRegistration();
    // Inoffensif si le worker ne l'écoute pas — la configuration actuelle appelle déjà
    // `skipWaiting()` d'elle-même. C'est une ceinture pour le jour où ce réglage change.
    registration?.waiting?.postMessage({ type: 'SKIP_WAITING' });
  } catch {
    // Peu importe : le rechargement suffit, le document passe par `NetworkFirst`.
  }
  location.reload();
}

export interface UpdateNoticeHandle {
  readonly el: HTMLElement;
  /** Affiche l'invitation. Sans effet si elle est déjà à l'écran. */
  show(): void;
  destroy(): void;
}

/**
 * Bandeau « Nouvelle version disponible » avec son bouton de rechargement.
 *
 * Posé EN HAUT, sous la barre d'en-tête, et non près du dock : la bannière d'installation
 * occupe déjà le bas de l'écran, et deux invitations qui se recouvrent ne valent pas mieux
 * qu'aucune. Un changement de version parle du produit entier, sa place est en tête.
 *
 * Refermable : une invitation qu'on ne peut pas écarter devient une nuisance. Elle ne
 * revient pas dans la même session — la surveillance n'annonce qu'une fois — mais un
 * rechargement, lui, la fera réapparaître si la version est toujours en retard.
 */
export function createUpdateNotice(): UpdateNoticeHandle {
  const el = document.createElement('aside');
  el.className = 'app-notice app-notice--top v-panel v-panel--floating';
  el.hidden = true;
  el.setAttribute('role', 'status');
  // `polite` et non `assertive` : la nouvelle est utile, elle n'est pas urgente, et elle
  // ne doit pas couper la parole à un lecteur d'écran en pleine phrase.
  el.setAttribute('aria-live', 'polite');

  const body = document.createElement('div');
  body.className = 'app-notice__body';

  const title = document.createElement('b');
  title.append(icon('bolt'), t('update.title'));

  const text = document.createElement('span');
  text.className = 'app-notice__text';
  text.textContent = t('update.text');

  body.append(title, text);

  const action = createButton({
    label: t('update.action'),
    variant: 'primary',
    size: 'sm',
    onClick: () => {
      void refresh();
    },
  });

  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'v-icon-btn app-notice__close';
  close.setAttribute('aria-label', t('common.close'));
  close.append(icon('close'));

  const onClose = (): void => {
    el.hidden = true;
  };
  close.addEventListener('click', onClose);

  el.append(body, action.el, close);

  return {
    el,
    show() {
      el.hidden = false;
    },
    destroy() {
      close.removeEventListener('click', onClose);
      el.remove();
    },
  };
}
